import { FC, FocusEventHandler, MouseEvent, useEffect, useState } from 'react';
import { FormControl, MenuItem, Select } from '@mui/material';
import { SelectChangeEvent } from '@mui/material/Select';
import { CloseOutlined as Close } from '@mui/icons-material';

import { CustomizedInput, InputBackgroundType } from '../Input';
import Text from '../Text';
import { RED, TEXT } from '../../Helpers/colors';
import { SelectDataProperties } from '.';
import { StyledChip } from './searchSelect';

interface MultipleSelectProperties {
	backgroundType?: InputBackgroundType;
	className?: string;
	data: SelectDataProperties[] | null;
	description?: string;
	disabled?: boolean;
	error?: boolean;
	errorMessage?: string;
	initialValue?: string[];
	label?: string;
	onBlur?: FocusEventHandler<HTMLInputElement>;
	placeholder?: string;
	setValue: (event: SelectChangeEvent<string[]>) => void;
}

const MultipleSelect: FC<MultipleSelectProperties> = ({
	data,
	setValue,
	onBlur,
	label = '',
	description = '',
	error = false,
	errorMessage = '',
	className = '',
	initialValue = [],
	disabled,
	backgroundType = 'primary',
}) => {
	const [open, setOpen] = useState<boolean>(false);
	const [values, setValues] = useState<string[]>([]);

	const handleChange = (event: SelectChangeEvent<string[]>) => {
		const { value } = event.target;
		if (!value || typeof value === 'string') return;
		const filteredValues = value.filter((x) => Boolean(x));
		setValues(filteredValues);
		const filteredEvent = {
			...event,
			target: {
				...event.target,
				value: filteredValues,
			},
		};
		setValue(filteredEvent as SelectChangeEvent<string[]>);
	};

	const handleOpen = () => {
		setOpen(true);
	};

	const handleClose = () => {
		setOpen(false);
	};

	const handleCloseButton = (event: MouseEvent<SVGSVGElement | HTMLLIElement>) => {
		event.stopPropagation();
		setOpen(false);
	};

	useEffect(() => {
		if (!initialValue?.length) return;
		setValues(initialValue);
	}, [initialValue]);

	return (
		<div className={className}>
			<FormControl variant="standard" style={{ width: '100%' }}>
				<Text
					className={`inputLabel ${label ? 'active' : ''}`}
					title={label}
					type="psm"
					fontWeight="medium"
					color={disabled ? TEXT.QUATERNARY : TEXT.PRIMARY}
				/>
				<Select
					multiple
					open={open}
					onClose={handleClose}
					onOpen={handleOpen}
					value={values}
					onChange={handleChange}
					onBlur={onBlur}
					disabled={disabled}
					input={
						<CustomizedInput
							disabled={disabled}
							error={error}
							backgroundType={backgroundType}
							style={{
								display: 'flex',
								flexWrap: 'wrap',
								alignItems: 'center',
								gap: 8,
								height: 'max-content',
							}}
						/>
					}
					startAdornment={
						<>
							{values.map((value) => {
								if (!data) return null;
								if (!value)
									return (
										<StyledChip key={value} label="N/A" className="marginBottom8" style={{ width: 'max-content' }} />
									);
								const item = data.find((x) => x.id === value);
								const chipLabel = item?.name ?? item?.text ?? item?.label;

								const removeItemHandler = () => {
									if (!values.includes(value) || Boolean(item?.isDefault)) return;
									const filteredValues = values.filter((x) => x !== value);
									setValues(filteredValues);
									const editedEvent: SelectChangeEvent<string[]> = {
										target: {
											value: filteredValues,
											name: '',
										},
									} as SelectChangeEvent<string[]>;
									setValue(editedEvent);
								};

								return (
									<StyledChip
										key={value}
										label={chipLabel}
										className="marginBottom8"
										style={{ width: 'max-content' }}
										clickable
										onDelete={() => removeItemHandler()}
										deleteIcon={<Close style={{ fontSize: '16px' }} />}
									/>
								);
							})}
						</>
					}
					renderValue={() => null}
				>
					<MenuItem onClick={handleCloseButton} style={{ position: 'absolute', top: 0, right: 0, zIndex: 1500 }}>
						<Close fontSize="small" />
					</MenuItem>
					{data?.map((item, index) => (
						<MenuItem disabled={item.isDefault} key={index} value={item.id}>
							<Text title={item.name ?? item.text ?? item.label ?? ''} type="psm" />
						</MenuItem>
					))}
				</Select>
				<Text
					className={`error ${error ? 'active' : ''}`}
					title={errorMessage}
					fontWeight="medium"
					type="psm"
					color={RED.BASE}
				/>
				<Text
					className={`inputDescription ${!error && Boolean(description) ? 'active' : ''}`}
					title={description}
					fontWeight="medium"
					type="psm"
				/>
			</FormControl>
		</div>
	);
};

export default MultipleSelect;
